import { memo, useCallback, useEffect, useEffectEvent, useMemo, useState, type ReactNode } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { useIsMinDesktopSize, useIsMinDesktopSizeL } from "~/common/hooks/useMediaQuery";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { getTariffNumbers } from "~/utils/productUtils";
import { useProductComparisonCardLogic } from "~/features/compareProducts/hooks/useProductComparisonCardLogic";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { CurrentPrice } from "~/domains/cart/components";
import { selectEarliestReplenishmentLeadTimeDate } from "~/utils/pna/availabilitySelectors";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { Accordion, AccordionSummary, Box, Stack, Typography } from "@schaeffler-commerce/design-system";
import { Price as PriceV3 } from "~/domains/pricing/components/Price/v3/Price";
import type { WarehouseStockData } from "~/domains/product/types/ProductData";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DateFormatType, formatDate, formatNullableDate } from "~/common/utils/DateFormatter";
import { PCAccordionDetails, PCTableContainer, ShowAllLess } from "./ComparisionTableStyles";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";

const RESULT_NF = NumberFormatter.getFormatter();
interface CommercialTableProps {
  products: ProductDescriptor[];
  placeholders: number[];
  showOnlyDifference: boolean;
}
export interface RowData {
  index: number;
}

type CommercialProduct = {
  netPrice?: string;
  listPrice?: string;
  cutomerSpecificDiscount?: string;
  inStock?: string;
  moreToComeStock?: string;
  replenishmentDate?: string;
  tariffNumber?: string;
};

type CommercialComparisonValues = {
  [key in keyof CommercialProduct]: string | number;
};

const LABEL_KEYS = [
  "productDetailsTable.netPrice",
  "productDetailsTable.listPrice",
  "productDetailsTable.csd",
  "productDetailsTable.inStock",
  "productDetailsTable.moreToComeStock",
  "productDetailsTable.replenishmentDate",
  "commercialTable.tariffNumber",
] as const;

const FIELD_KEYS: (keyof CommercialProduct)[] = [
  "netPrice",
  "listPrice",
  "cutomerSpecificDiscount",
  "inStock",
  "moreToComeStock",
  "replenishmentDate",
  "tariffNumber",
];

const getMoreToComeStockInfo = (wh: WarehouseStockData | undefined, clarificationStatus = "") => {
  const stockInfo: [string, string][] = [];
  if (!wh) {
    return stockInfo;
  }
  wh.inProduction.forEach((stock) => {
    stockInfo.push(["" + stock.quantity, stock.date ? formatDate(stock.date, DateFormatType.DATE) : ""]);
  });
  if (wh.inClarification > 0) {
    stockInfo.push(["" + wh.inClarification, clarificationStatus]);
  }

  return stockInfo;
};

const getInStockInfo = (wh: WarehouseStockData | undefined): [string, string][] =>
  wh?.inStock.map((stock) => ["" + stock.quantity, stock.date ? formatDate(stock.date, DateFormatType.DATE) : ""]) ??
  [];

const getInStockQuantity = (wh: WarehouseStockData | undefined) =>
  wh?.inStock.reduce((total, stock) => total + stock.quantity, 0) ?? 0;

const getMoreToComeStockQuantity = (wh: WarehouseStockData | undefined) =>
  (wh?.inProduction.reduce((total, stock) => total + stock.quantity, 0) ?? 0) + (wh?.inClarification ?? 0);

const renderStockTable = (data: [ReactNode, ReactNode][], showExpanded: boolean) => {
  const displayData = data.length > 6 && !showExpanded ? data.slice(0, 6) : data;

  return displayData.map((row, index) => (
    <Stack
      key={index}
      direction="row"
      sx={{
        gap: 2,
      }}
    >
      <Box sx={{ width: "100px" }}>
        <Typography sx={{ width: "50px" }}>{row[0]}</Typography>
      </Box>

      <Box
        sx={{
          textAlign: "right",
        }}
      >
        {row[1]}
      </Box>
    </Stack>
  ));
};

const CommercialProductCell = ({
  product,
  field,
  isExpandedStockTable,
}: {
  product: ProductDescriptor;
  field: keyof CommercialProduct;
  isExpandedStockTable: boolean;
}) => {
  const { price, listPrice, tariffSurcharge, hasSurcharge, netPrice } = useProductComparisonCardLogic(
    product?.base?.code,
  );
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const { availability: availabilityInfo } = product;
  const replenishmentDate = selectEarliestReplenishmentLeadTimeDate(availabilityInfo);
  const warehouse = product?.availability?.packagingInformation[0]?.totalWarehouse;

  switch (field) {
    case "netPrice":
      return (
        <CurrentPrice
          price={price}
          zeroText={t("product.priceOnRequest")}
          tariffSurcharge={tariffSurcharge}
          surchargeEnabled={hasSurcharge}
          style={{ fontWeight: "normal" }}
        />
      );

    case "tariffNumber": {
      const tariffNumber = getTariffNumbers(product.base).join(", ");
      return tariffNumber || "-";
    }
    case "cutomerSpecificDiscount":
      return netPrice ? <PriceV3.ExtraLabels price={netPrice} surchargeEnabled={hasSurcharge} /> : "-";

    case "inStock": {
      const totalInStock = getInStockQuantity(warehouse);
      if (totalInStock === 0) {
        return t("stockIndicator.currentlyNotInStock");
      }

      if (!isDesktop) {
        return RESULT_NF.format(totalInStock);
      }

      return renderStockTable(getInStockInfo(warehouse), isExpandedStockTable);
    }

    case "moreToComeStock": {
      if (!isDesktop) {
        const totalMoreToComeStock = getMoreToComeStockQuantity(warehouse);
        return totalMoreToComeStock > 0 ? RESULT_NF.format(totalMoreToComeStock) : "-";
      }

      const stockData = getMoreToComeStockInfo(warehouse, t("product.availability.stock.table.inClarification"));
      return stockData.length > 0 ? renderStockTable(stockData, isExpandedStockTable) : "-";
    }

    case "replenishmentDate":
      return formatNullableDate(replenishmentDate, DateFormatType.DATE);

    case "listPrice":
      return listPrice ? <PriceElements.Price price={listPrice} /> : "-";

    default:
      return null;
  }
};

const CommercialProductComparisonValue = ({
  product,
  onValue,
}: {
  product: ProductDescriptor;
  onValue: (value: CommercialComparisonValues) => void;
}) => {
  const { price, listPrice, netPrice } = useProductComparisonCardLogic(product?.base?.code);
  const warehouse = product?.availability?.packagingInformation?.[0]?.totalWarehouse;
  const replenishmentDate = selectEarliestReplenishmentLeadTimeDate(product.availability);

  // react-doctor:SYNC-EXTERNAL: reporting freshly loaded comparison values to the parent is a reaction
  // to async data, not a user event; the publish goes through a useEffectEvent so onValue identity
  // changes cannot re-report. Trigger deps stay value-level (price/stock scalars keyed by
  // warehouse?.inStock) — whole-object deps would re-report on unrelated identity churn.
  const publishValues = useEffectEvent(() =>
    onValue({
      netPrice: price?.value ?? "",
      listPrice: listPrice?.value ?? "",
      cutomerSpecificDiscount: netPrice?.value ?? "",
      inStock: getInStockInfo(warehouse)
        .map(([quantity, date]) => `${quantity}:${date}`)
        .join(","),
      moreToComeStock: getMoreToComeStockInfo(warehouse)
        .map(([quantity, date]) => `${quantity}:${date}`)
        .join(","),
      replenishmentDate: replenishmentDate?.toString() ?? "",
      tariffNumber: getTariffNumbers(product.base).join(","),
    }),
  );
  useEffect(() => {
    publishValues();
  }, [price?.value, listPrice?.value, netPrice?.value, warehouse?.inStock, replenishmentDate, product.base.code]);
  return null;
};

const CommercialTable = memo(({ products, placeholders, showOnlyDifference }: CommercialTableProps) => {
  const [isExpandedStockTable, setIsExpandedStockTable] = useState(false);
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  const isDesktop = useIsMinDesktopSize();
  const [comparisonValues, setComparisonValues] = useState<Record<number, CommercialComparisonValues>>({});
  const hasPricePermission = userHasPermission(Permission.PRICE);
  const hasListPricePermission = userHasPermission(Permission.SEE_LIST_PRICE);
  const hasAvailabilityPermission = userHasPermission(Permission.AVAILABILITY);
  const hasDiscountPermission = userHasPermission(Permission.SEE_DISCOUNT);

  const tableData = useMemo(() => {
    const tableRows: RowData[] = [
      ...(hasPricePermission ? [{ index: 0 }] : []),
      ...(hasListPricePermission ? [{ index: 1 }] : []),
      ...(hasDiscountPermission ? [{ index: 2 }] : []),
      ...(hasAvailabilityPermission ? [{ index: 3 }, { index: 4 }, { index: 5 }] : []),
      { index: 6 },
    ];

    if (!showOnlyDifference) {
      return tableRows;
    }

    return tableRows.filter((row) => {
      const field = FIELD_KEYS[row.index];

      const values = Object.values(comparisonValues).map((product) => product[field]);

      return new Set(values).size > 1;
    });
  }, [
    comparisonValues,
    hasAvailabilityPermission,
    hasDiscountPermission,
    hasListPricePermission,
    hasPricePermission,
    showOnlyDifference,
  ]);

  const hasCommercialData = useMemo(() => {
    return Object.values(comparisonValues).some((product) =>
      FIELD_KEYS.some((field) => {
        const value = product[field];

        return value !== "" && value !== 0 && value != null;
      }),
    );
  }, [comparisonValues]);

  const columns = useMemo<ColumnDef<RowData>[]>(() => {
    const labelColumn: ColumnDef<RowData> = {
      id: "label",
      size: 246,
      cell: ({ row }) => {
        const index = row.original.index;

        if (index === 3 || index === 4) {
          const hasStockData = products.some((product) => {
            const warehouse = product.availability?.packagingInformation?.[0]?.totalWarehouse;
            const stockInfo = index === 3 ? getInStockInfo(warehouse) : getMoreToComeStockInfo(warehouse);
            return stockInfo.length > 6;
          });

          return (
            <Stack>
              <Typography>{t(LABEL_KEYS[index])}</Typography>

              {hasStockData && (
                <ShowAllLess onClick={() => setIsExpandedStockTable((prev) => !prev)}>
                  {isExpandedStockTable ? (
                    <>
                      {t("searchPage.refinements.showLess")}
                      <KeyboardArrowUpIcon />
                    </>
                  ) : (
                    <>
                      {t("availabilityTable.showAll")}
                      <KeyboardArrowDownIcon />
                    </>
                  )}
                </ShowAllLess>
              )}
            </Stack>
          );
        }

        return t(LABEL_KEYS[index]);
      },
    };

    const productColumns = [...products.map((_, index) => index), ...placeholders].map((index) => ({
      id: `product-${index}`,
      header: "",
      size: 250,

      cell: ({ row }: { row: { original: RowData } }) => {
        const product = products[index];

        if (!product) {
          return null;
        }

        const rowIndex = row.original.index;

        return (
          <Stack>
            <CommercialProductCell
              product={product}
              field={FIELD_KEYS[rowIndex]}
              isExpandedStockTable={isExpandedStockTable}
            />

            {!isDesktop && (
              <Typography variant="bodySmall" color="textSecondary">
                {t(LABEL_KEYS[rowIndex])}
              </Typography>
            )}
          </Stack>
        );
      },
    }));

    const allColumns: ColumnDef<RowData>[] = [];

    if (isDesktop) {
      allColumns.push(labelColumn);
    }

    allColumns.push(...productColumns);

    if (isDesktopL && allColumns.length > 0) {
      const lastIndex = allColumns.length - 1;

      allColumns[lastIndex] = {
        ...allColumns[lastIndex],
        size: undefined,
      };
    }

    return allColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- INTENTIONAL-STALE: t is stable per page load (language switch performs a full window navigation)
  }, [products, placeholders, isDesktop, isDesktopL, isExpandedStockTable]);

  const handleComparisonValue = useCallback((index: number, value: CommercialComparisonValues) => {
    setComparisonValues((prev) => ({
      ...prev,
      [index]: value,
    }));
  }, []);

  const comparisonValueHandlers = useMemo(() => {
    return products.map((_, index) => {
      return (value: CommercialComparisonValues) => {
        handleComparisonValue(index, value);
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- INTENTIONAL-STALE: index-keyed closures over a stable setter; keyed to products.length which is the only real input
  }, [products.length, handleComparisonValue]);

  //  Not display the commercial data if we don't have
  if (Object.keys(comparisonValues).length === products.length && !hasCommercialData) {
    return null;
  }

  if (Object.keys(comparisonValues).length === products.length && tableData.length === 0) {
    return null;
  }

  return (
    <>
      {products.map((product, index) => (
        <CommercialProductComparisonValue
          key={product.base.code}
          product={product}
          onValue={comparisonValueHandlers[index]}
        />
      ))}

      <Accordion defaultExpanded>
        <AccordionSummary>
          <Typography>{t("commercialTable.tableName")}</Typography>
        </AccordionSummary>

        <PCAccordionDetails>
          <PCTableContainer>
            <TanStackTable
              key={products.length}
              data={tableData}
              columns={columns}
              variant="productComparison"
              state={{
                columnPinning: {
                  left: ["label"],
                },
              }}
            />
          </PCTableContainer>
        </PCAccordionDetails>
      </Accordion>
    </>
  );
});

export default CommercialTable;
